import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import RightSideBarForm from "../rigthSidebarForm";
import { useDispatch, useSelector } from "react-redux";
import { GetExcelCashFlow } from "src/graphql/cashflow";
import { importUpdateCashflowNeeded } from "src/redux/actions/flujocaja";
import { importAlertState } from "src/redux/actions/alert";
import { toAbsoluteDownloadUrl } from "src/utils/toAbsoluteDownloadUrl";

function sanitizeCompanyNameForFile(companyName) {
  const normalized = String(companyName || "Empresa")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9\s_-]/g, "")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .substring(0, 50);
  return normalized || "Empresa";
}

function buildCashFlowFileName(companyName) {
  return `flujo_de_caja_${sanitizeCompanyNameForFile(companyName)}.xlsx`;
}

/**
 * Fuerza la descarga al directorio Descargas del usuario.
 * El nombre visible del archivo usa el de la empresa (no un timestamp).
 */
async function downloadExportFile(url, fileName) {
  const absoluteUrl = toAbsoluteDownloadUrl(url);
  if (!absoluteUrl) return false;
  const downloadName = fileName || "flujo_de_caja.xlsx";

  try {
    const response = await fetch(absoluteUrl, { mode: "cors", credentials: "omit" });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = downloadName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(blobUrl);
    return true;
  } catch {
    // Fallback: iframe + Content-Disposition de la URL firmada
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = absoluteUrl;
    document.body.appendChild(iframe);
    window.setTimeout(() => iframe.remove(), 60_000);
    return true;
  }
}

export default function ExportCashFlow({ closeMenu }) {
  const { t, i18n } = useTranslation("finance");

  const { selectedCompany } = useSelector((state) => state.companyList);
  const dispatch = useDispatch();
  const companyId = selectedCompany.id;
  const companyName = selectedCompany?.name || selectedCompany?.alias || "Empresa";
  const [form, setForm] = useState({ view: "week", currency: "CLP" });
  const currency = form?.currency || "CLP";
  const view = form?.view || "week";
  const { executeMutation: downloadExcel, fetching: downloadCashFLowExcel } =
    GetExcelCashFlow();

  const periodicidad = useMemo(
    () => [
      { value: "week", text: t("cashflow.weekly") },
      { value: "month", text: t("cashflow.monthly") },
      { value: "day", text: t("cashflow.daily") },
    ],
    [t, i18n.language],
  );

  const currencyOptions = useMemo(
    () => [
      { key: "CLP", value: "CLP", flag: "cl", text: t("exportCashflowForm.currencyCLP") },
      { key: "USD", value: "USD", flag: "us", text: t("exportCashflowForm.currencyUSD") },
      { key: "EUR", value: "EUR", flag: "eu", text: t("exportCashflowForm.currencyEUR") },
      { key: "UF", value: "UF", flag: "cl", text: t("exportCashflowForm.currencyUF") },
      { key: "UTM", value: "UTM", flag: "cl", text: t("exportCashflowForm.currencyUTM") },
    ],
    [t, i18n.language],
  );

  const handleSubmit = async () => {
    if (!companyId) {
      dispatch(
        importAlertState({
          content: t("exportCashflowForm.missingCompany"),
          state: "danger",
          duration: 5,
        }),
      );
      return;
    }

    const response = await downloadExcel(
      { companyId, currency, view, showProjections: true },
      { requestPolicy: "network-only" },
    );

    if (response?.error) {
      dispatch(
        importAlertState({
          content: response.error.message || t("exportCashflowForm.exportError"),
          state: "danger",
          duration: 6,
        }),
      );
      return;
    }

    const url = response?.data?.CashFlowAsExcel;
    if (!url) {
      dispatch(
        importAlertState({
          content: t("exportCashflowForm.missingUrl"),
          state: "danger",
          duration: 5,
        }),
      );
      return;
    }

    await downloadExportFile(url, buildCashFlowFileName(companyName));
    dispatch(
      importAlertState({
        content: t("exportCashflowForm.exportSuccess"),
        state: "success",
        duration: 4,
      }),
    );
    closeMenu();
  };

  const onChange = (data) => {
    setForm(data);
    dispatch(importUpdateCashflowNeeded({ value: false, reload: true }));
  };

  const section = useMemo(
    () => [
      {
        title: t("exportCashflowForm.sectionTitle"),
        display: true,
        subSection: [
          {
            required: true,
            type: "Dropdown",
            label: t("exportCashflowForm.periodicity"),
            placeholder: t("exportCashflowForm.periodicityPlaceholder"),
            value: form?.view || "week",
            options: periodicidad,
            name: "view",
            search: true,
            display: true,
          },
          {
            required: true,
            type: "Dropdown",
            label: t("exportCashflowForm.currency"),
            placeholder: t("exportCashflowForm.currencyPlaceholder"),
            value: form?.currency || "CLP",
            options: currencyOptions,
            name: "currency",
            search: true,
            display: true,
          },
        ],
      },
    ],
    [t, form?.view, form?.currency, periodicidad, currencyOptions],
  );

  return (
    <RightSideBarForm
      section={section}
      handleSubmit={handleSubmit}
      onChange={onChange}
      initialData={form}
      onOkText={t("exportCashflowForm.exportButton")}
      loading={downloadCashFLowExcel}
    />
  );
}
